import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  ReferenceArea,
} from 'recharts';
import { useAppSelector } from 'store/hook';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { type preferredTimeDisplayModeTypes } from 'types/customer.types';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import type {
  MLInsight,
  SupportingEvidenceResponse,
  AirFilterEvidenceResponse,
  AirFilterDataPoint,
  AirFilterTooltipPayload,
  FilterVisibilityState,
} from 'types/ml-insights.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface SupportingEvidenceAirFilterProps {
  data: MLInsight;
  evidence: SupportingEvidenceResponse | AirFilterEvidenceResponse | null;
  enableDarkTheme: boolean;
  onPointClick?: () => void;
  showDetailChart?: boolean;
  onToggleDetailChart?: () => void;
}

interface TooltipProps {
  active?: boolean;
  payload?: AirFilterTooltipPayload[];
  enableDarkTheme: boolean;
  onPointClick?: () => void;
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes;
  timezone: string;
  unitSystem: string;
}

const CustomTooltip: React.FC<TooltipProps> = ({
  active,
  payload,
  enableDarkTheme,
  preferredTimeDisplayMode,
  timezone,
  unitSystem,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  if (active && payload && payload.length) {
    const firstPayload = payload[0];

    const convertedTime = convertUtcToDisplayTime(
      firstPayload.payload?.sample_timestamp ?? '',
      preferredTimeDisplayMode,
      timezone
    );
    return (
      <Box
        sx={{
          backgroundColor: enableDarkTheme
            ? palette.tableHeader
            : palette.toolBarBackground,
          padding: '10px',
          borderRadius: '5px',
          minWidth: '200px',
        }}
      >
        <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>
          {convertedTime}
        </Typography>
        {payload.map((entry: AirFilterTooltipPayload, index: number) => (
          <Typography key={index} sx={{ fontSize: '12px', color: entry.color }}>
            {entry.name}: {entry.value?.toFixed(2)}{' '}
            {unitSystem === 'metric' ? 'kPa' : 'psi'}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

export const SupportingEvidenceAirFilter: React.FC<
  SupportingEvidenceAirFilterProps
> = ({
  data,
  evidence,
  enableDarkTheme,
  onPointClick,
  showDetailChart,
  onToggleDetailChart,
}) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { isEvidenceLoading } = useAppSelector(
    (state) => state.mlInsightsReducer
  );
  const unitSystem =
    useAppSelector((state) => state.authReducer.customer?.unitSystem) ??
    'metric';

  const { timezone, preferredTimeDisplayMode } = useTimeDisplayMode();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  /**
   * Axis ticks must use the same UTC -> display-timezone conversion as the
   * tooltip. `sample_timestamp` has no zone suffix, so parsing it directly
   * would treat the UTC reading as local time and mislabel every tick.
   */
  const formatAxisTimestamp = (value: string) =>
    convertUtcToDisplayTime(
      value,
      preferredTimeDisplayMode,
      timezone,
      'MM/DD HH:mm'
    );

  // Health-zone threshold (kPa): Healthy 0–3.5, Approaching EOL 3.5–7, EOL 7+.
  const HEALTHY_THRESHOLD = 3.5;

  const rawChartData =
    (evidence?.evidenceValues as unknown as AirFilterDataPoint[]) ?? [];

  // Only show timestamps where at least one filter reaches Approaching EOL / EOL.
  // Fully-healthy points (all filter values below the threshold) are dropped.
  // Athena returns rows newest-first, so re-sort ascending — recharts plots in
  // array order and would otherwise draw the time axis right-to-left.
  const chartData = rawChartData
    .filter((item: AirFilterDataPoint) =>
      [
        item.j1939engineairfilter1differentialpressure,
        item.j1939engineairfilter2differentialpressure,
        item.j1939engineairfilter3differentialpressure,
        item.j1939engineairfilter4differentialpressure,
      ].some((value) => typeof value === 'number' && value >= HEALTHY_THRESHOLD)
    )
    .sort((a: AirFilterDataPoint, b: AirFilterDataPoint) =>
      (a.sample_timestamp ?? '').localeCompare(b.sample_timestamp ?? '')
    );

  const HEALTHY_COLOR = '#4CAF50'; // Green
  const APPROACHING_EOL_COLOR = '#FFC107'; // Yellow
  const EOL_COLOR = '#F44336'; // Red

  // Line colors for different filters
  const FILTER_COLORS = {
    filter1: '#2196F3', // Blue
    filter2: '#9C27B0', // Purple
    filter3: '#FF9800', // Orange
    filter4: '#00BCD4', // Cyan
  };

  const [visibleLines, setVisibleLines] = React.useState<FilterVisibilityState>(
    {
      filter1: true,
      filter2: true,
      filter3: true,
      filter4: true,
    }
  );

  const toggleLineVisibility = (filterKey: string) => {
    setVisibleLines((prev) => ({
      ...prev,
      [filterKey]: !prev[filterKey as keyof typeof prev],
    }));
  };

  if (!chartData || (chartData.length === 0 && !isEvidenceLoading)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        }}
      >
        <Typography>{t('no_supporting_evidence_available')}</Typography>
      </Box>
    );
  }

  const maxValue = Math.max(
    ...chartData.map((item: AirFilterDataPoint) =>
      Math.max(
        item.j1939engineairfilter1differentialpressure ?? 0,
        item.j1939engineairfilter2differentialpressure ?? 0,
        item.j1939engineairfilter3differentialpressure ?? 0,
        item.j1939engineairfilter4differentialpressure ?? 0
      )
    )
  );
  const yAxisMax = Math.max(10, maxValue + 1);

  return (
    <Box
      sx={{
        height: '540px',
        backgroundColor: palette.boxBackground,
        borderRadius: '8px',
        border: `1px solid ${
          enableDarkTheme ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'
        }`,
        boxShadow: enableDarkTheme
          ? '0 4px 12px rgba(0, 0, 0, 0.3)'
          : '0 4px 12px rgba(0, 0, 0, 0.1)',
        overflow: 'scroll',
      }}
    >
      {isEvidenceLoading ? (
        <Box sx={{ padding: '5%', height: '100%', marginTop: '20%' }}>
          <SingleFadeLoader isFullScreen={false} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px 12px 20px',
              borderBottom: `1px solid ${
                enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.08)'
              }`,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: '700',
                color: palette.textPrimary,
              }}
            >
              {t('air_filter_differential_pressure')}
            </Typography>

            {onToggleDetailChart && (
              <IconButton
                onClick={onToggleDetailChart}
                sx={{
                  padding: '4px',
                  color: palette.textPrimary,
                  '&:hover': {
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  transform: showDetailChart
                    ? 'rotate(0deg)'
                    : 'rotate(180deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
                title={
                  showDetailChart ? 'Hide detail chart' : 'Show detail chart'
                }
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              padding: '20px',
              height: 'calc(100% - 105px)',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="healthyZone" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={HEALTHY_COLOR}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="100%"
                      stopColor={HEALTHY_COLOR}
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                  <linearGradient
                    id="approachingEolZone"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={APPROACHING_EOL_COLOR}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="100%"
                      stopColor={APPROACHING_EOL_COLOR}
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                  <linearGradient id="eolZone" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={EOL_COLOR} stopOpacity={0.3} />
                    <stop
                      offset="100%"
                      stopColor={EOL_COLOR}
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="sample_timestamp"
                  tickFormatter={(value) => formatAxisTimestamp(value)}
                  style={{ fontSize: '12px' }}
                  interval={isSmallScreen ? 'equidistantPreserveStart' : 100}
                />
                <YAxis
                  domain={[0, yAxisMax]}
                  label={{
                    value: `Pressure (${
                      unitSystem === 'metric' ? 'kPa' : 'psi'
                    })`,
                    angle: -90,
                    position: 'insideLeft',
                  }}
                  style={{ fontSize: '12px' }}
                />
                <Tooltip
                  content={
                    <CustomTooltip
                      enableDarkTheme={enableDarkTheme}
                      preferredTimeDisplayMode={preferredTimeDisplayMode}
                      timezone={timezone}
                      unitSystem={unitSystem}
                    />
                  }
                />

                {/* Background zones */}
                {/* Healthy zone: 0 to 3.5 */}
                <ReferenceArea
                  y1={0}
                  y2={3.5}
                  fill="url(#healthyZone)"
                  fillOpacity={1}
                  label={{
                    value: 'Healthy',
                    position: 'insideTopLeft',
                    fill: HEALTHY_COLOR,
                    fontSize: 12,
                  }}
                />
                {/* Approaching EOL zone: 3.5 to 7 */}
                <ReferenceArea
                  y1={3.5}
                  y2={7}
                  fill="url(#approachingEolZone)"
                  fillOpacity={1}
                  label={{
                    value: 'Approaching EOL',
                    position: 'insideTopLeft',
                    fill: APPROACHING_EOL_COLOR,
                    fontSize: 12,
                  }}
                />
                {/* EOL zone: 7 to max */}
                <ReferenceArea
                  y1={7}
                  y2={yAxisMax}
                  fill="url(#eolZone)"
                  fillOpacity={1}
                  label={{
                    value: 'EOL',
                    position: 'insideTopLeft',
                    fill: EOL_COLOR,
                    fontSize: 12,
                  }}
                />

                {/* Data lines for each filter */}
                {visibleLines.filter1 && (
                  <Area
                    type="monotone"
                    dataKey="j1939engineairfilter1differentialpressure"
                    stroke={FILTER_COLORS.filter1}
                    fill="none"
                    strokeWidth={2}
                    name="Filter 1"
                    dot={false}
                    connectNulls
                  />
                )}
                {visibleLines.filter2 && (
                  <Area
                    type="monotone"
                    dataKey="j1939engineairfilter2differentialpressure"
                    stroke={FILTER_COLORS.filter2}
                    fill="none"
                    strokeWidth={2}
                    name="Filter 2"
                    dot={false}
                    connectNulls
                  />
                )}
                {visibleLines.filter3 && (
                  <Area
                    type="monotone"
                    dataKey="j1939engineairfilter3differentialpressure"
                    stroke={FILTER_COLORS.filter3}
                    fill="none"
                    strokeWidth={2}
                    name="Filter 3"
                    dot={false}
                    connectNulls
                  />
                )}
                {visibleLines.filter4 && (
                  <Area
                    type="monotone"
                    dataKey="j1939engineairfilter4differentialpressure"
                    stroke={FILTER_COLORS.filter4}
                    fill="none"
                    strokeWidth={2}
                    name="Filter 4"
                    dot={false}
                    connectNulls
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>

            {/* Legend */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                mt: 2,
                flexWrap: 'wrap',
              }}
            >
              {Object.entries(FILTER_COLORS).map(([key, color]) => {
                const isVisible =
                  visibleLines[key as keyof typeof visibleLines];
                return (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      opacity: isVisible ? 1 : 0.4,
                      transition: 'opacity 0.2s ease',
                      '&:hover': {
                        opacity: isVisible ? 0.8 : 0.6,
                      },
                    }}
                    onClick={() => {
                      toggleLineVisibility(key);
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 3,
                        backgroundColor: color,
                        textDecoration: isVisible ? 'none' : 'line-through',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '10px',
                        textDecoration: isVisible ? 'none' : 'line-through',
                      }}
                    >
                      {(() => {
                        switch (key) {
                          case 'filter1':
                            return 'Filter 1 (j1939engineairfilter1differentialpressure)';
                          case 'filter2':
                            return 'Filter 2 (j1939engineairfilter2differentialpressure)';
                          case 'filter3':
                            return 'Filter 3 (j1939engineairfilter3differentialpressure)';
                          case 'filter4':
                            return 'Filter 4 (j1939engineairfilter4differentialpressure)';
                          default:
                            return '';
                        }
                      })()}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
